export type UserRole = "admin" | "partner" | "installer" | "commercial";

/** Type de tarif pour le budget (neuf vs remplacement). */
export type BudgetTariffType = "neuf" | "remplacement";

/** Prix unitaire budget par région x référence x type (neuf / remplacement). */
export interface BudgetPrice {
  id: string;
  stockRegionId: string;
  inventoryId: string;
  tariffType: BudgetTariffType;
  unitPriceCents: number;
  createdAt?: string;
  updatedAt?: string;
}

/** Région de stock : un stock par région (ex. Paris, Lyon). */
export interface StockRegion {
  id: string;
  name: string;
  ownerId: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface StockItem {
  id: string;
  name: string;
  quantity: number;
  lowStockThreshold?: number; // Seuil d'alerte stock bas
  /** Région à laquelle appartient cet article. */
  regionId?: string;
  /** Partenaire propriétaire de ce stock (NULL = stock interne). */
  partnerId?: string | null;
}

export interface Job {
  id: string;
  clientName: string;
  clientPhone: string;
  clientAddress: string;
  status: "pending" | "scheduled" | "in_progress" | "completed" | "cancelled";
  date?: string;
  time?: string;
  installerId?: string;
  /** Commercial assigné au dossier / rendez-vous. */
  commercialId?: string;
  partnerId?: string;
  /** Chemins Storage des documents du dossier (bucket devis_signes). */
  signedQuoteUrls?: string[];
  /** Admin ou équipier ayant créé le dossier (quand pas de partenaire). */
  createdBy?: string;
  notes?: string;
  surface?: string;
  lightingType?: string;
  ceilingHeight?: string;
  /** Nombre de LED estimé pour l'installation (saisi par le partenaire). */
  estimatedLedCount?: string;
  stockUsed?: Record<string, number>;
  /** Quantités posées en neuf par référence (itemId → nombre). */
  stockUsedNeuf?: Record<string, number>;
  /** Quantités posées en remplacement par référence (itemId → nombre). */
  stockUsedRemplacement?: Record<string, number>;
  /** Montant facture partenaire calculé à la validation (centimes). */
  partnerInvoiceTotalCents?: number | null;
  /** Quantités de matériel à préparer (assignation admin → visible par l'installateur). */
  stockToPrepare?: Record<string, number>;
  /** Région de stock utilisée pour ce dossier (préparation / consommation). */
  stockRegionId?: string;
  /** Type de dossier : LED ext/int, déshumidificateur, VMC Agricoles, TH 122, rénovation, HP/BP flottante, PAC. */
  dossierType?:
    | "led"
    | "led_exterieur"
    | "led_interieur"
    | "deshumidificateur"
    | "vmc_agricoles"
    | "th_122"
    | "renovation"
    | "hp_bp_flottante"
    | "pac";
  /** Nombre de déshumidificateurs à installer. */
  dehumidifierCount?: number;
  /** Nombre de minuteries TH 122 à installer. */
  timerCount?: number;
  /** Option déshumidificateur : compteur. */
  needsCounter?: boolean;
  /** Option déshumidificateur : générateur. */
  needsGenerator?: boolean;
  installerNotes?: string;
  createdAt: string;
}

export interface User {
  id: string;
  name: string;
  role: UserRole;
  code: string;
  email?: string;
  phone?: string;
  company?: string;
  active: boolean;
  /** Admin principal (peut gérer l'équipe). */
  is_owner?: boolean;
  team_role?: TeamRole;
  permissions?: TeamPermissions;
  /** Pour partenaire/poseur : id de l'admin (tenant) qui les a créés. */
  owner_id?: string | null;
  /** URL de l'avatar (photo de profil) */
  avatar_url?: string | null;
  /** Préférences utilisateur */
  preferences?: UserPreferences;
}

export interface DossierNote {
  id: string;
  dossierId: string;
  userId: string;
  userRole: UserRole;
  userName: string;
  content: string;
  createdAt: string;
}

/** Préférences utilisateur pour notifications et paramètres */
export interface UserPreferences {
  /** Alerte globale : activer/désactiver les notifications dans l'app . Clé conservée pour rétrocompat. */
  emailNotifications?: boolean;
  newJobsNotifications?: boolean;
  /** Informations entreprise (owner) pour les documents */
  companyName?: string;
  companySiret?: string;
  companyAddress?: string;
  companyPhone?: string;
  /** Pour les poseurs uniquement : si true (défaut), ce poseur peut voir le partenaire qui a apporté chaque chantier. */
  canSeePartner?: boolean;
  /**
   * Pour les partenaires uniquement : si true, le partenaire peut définir ou modifier date/heure
   * (ses dossiers uniquement : en attente ou programmés), via l'espace partenaire.
   */
  canPartnerManageOwnSchedule?: boolean;
  /** Pour les poseurs uniquement : région de stock assignée par défaut (affichage dans l’onglet Poseurs). */
  defaultStockRegionId?: string;
  /** Pour les admins : afficher la bulle "mini secrétaire" à l'entrée sur le CRM (défaut true). */
  miniSecretaryEnabled?: boolean;
  /** Pour les owners : visite guidée terminée (onboarding). */
  onboardingDone?: boolean;
  /** Guides "nouvelle fonctionnalité" déjà vus : { [featureId]: true } = ne plus afficher. */
  dismissedFeatureGuides?: Record<string, boolean>;
  /** Langue de l'interface : fr | en | es */
  locale?: "fr" | "en" | "es";
  /** Template personnalisé devis/facture (owner) : thème, couleur, pied de page */
  invoiceTemplate?: InvoiceTemplate;
}

/** Template personnalisé pour les PDF devis/facture */
export interface InvoiceTemplate {
  /** Thème visuel : minimal (épuré), classic (bordures), luxe (accent fort) */
  theme?: "minimal" | "classic" | "luxe";
  /** Couleur d'accent (hex, ex. #1d4ed8) pour titres et lignes */
  accentColor?: string;
  /** Texte personnalisé en pied de page (remplace le texte par défaut si défini) */
  footerText?: string;
  /** Utiliser la photo de profil (owner) comme logo sur le document */
  useProfilePhotoAsLogo?: boolean;
  /** URL du logo entreprise (optionnel; ignoré si useProfilePhotoAsLogo) */
  logoUrl?: string;
  /** Scripts : texte d'introduction (avant Client / Date) */
  introText?: string;
  /** Scripts : conditions générales / mentions (après le total, avant le pied de page) */
  termsText?: string;
  /** Mise en page : orientation de la page */
  pageOrientation?: "portrait" | "landscape";
  /** Mise en page : taille du texte (small, normal, large) */
  fontSize?: "small" | "normal" | "large";
  /** Mise en page : position du logo (gauche ou droite) */
  logoPosition?: "left" | "right";
}

/** Notification de la mini secrétaire (bulle + panneau bot). */
export interface SecretaryNotification {
  id: string;
  type: "pending_jobs" | "completed_jobs" | "low_stock";
  shortMessage: string;
  fullMessage: string;
  action: { tab: string; label: string; regionId?: string; partnerId?: string };
}

export interface Log {
  id: string;
  action: string;
  user: string;
  timestamp: string;
  details: string;
}

/** Permissions d'un membre d'équipe (stock, dossiers, accès, équipe). */
export interface TeamPermissions {
  canViewJobs?: boolean;
  canCreateJobs?: boolean;
  canEditJobs?: boolean;
  canDeleteJobs?: boolean;
  canManageStock?: boolean;
  canManagePartners?: boolean;
  canManageInstallers?: boolean;
  canViewMainHistory?: boolean;
  /** Voir/gérer l'onglet Mon Équipe (liste en lecture seule si pas owner). */
  canManageTeam?: boolean;
}

export type TeamRole = "owner" | "admin" | "member";

/** Schéma Supabase (snake_case). Ne pas modifier sauf critique. */
export interface ProfileRow {
  id: string;
  name: string;
  role: UserRole;
  code: string;
  email?: string | null;
  phone?: string | null;
  company?: string | null;
  active: boolean;
  created_at?: string;
  updated_at?: string;
  owner_id?: string | null;
  is_owner?: boolean;
  team_role?: TeamRole | null;
  permissions?: TeamPermissions | null;
  avatar_url?: string | null;
  preferences?: UserPreferences | null;
}

export interface JobRow {
  id: string;
  client_name: string;
  client_phone: string;
  client_address: string;
  status: "pending" | "scheduled" | "in_progress" | "completed" | "cancelled";
  date?: string | null;
  time?: string | null;
  installer_id?: string | null;
  commercial_id?: string | null;
  signed_quote_url?: string[] | null;
  partner_id?: string | null;
  created_by?: string | null;
  notes?: string | null;
  surface?: string | null;
  lighting_type?: string | null;
  ceiling_height?: string | null;
  estimated_led_count?: string | null;
  stock_used?: Record<string, number> | null;
  stock_used_neuf?: Record<string, number> | null;
  stock_used_remplacement?: Record<string, number> | null;
  partner_invoice_total_cents?: number | null;
  stock_to_prepare?: Record<string, number> | null;
  stock_region_id?: string | null;
  dossier_type?:
    | "led"
    | "led_exterieur"
    | "led_interieur"
    | "deshumidificateur"
    | "vmc_agricoles"
    | "th_122"
    | "renovation"
    | "hp_bp_flottante"
    | "pac"
    | null;
  dehumidifier_count?: number | null;
  timer_count?: number | null;
  needs_counter?: boolean | null;
  needs_generator?: boolean | null;
  installer_notes?: string | null;
  created_at: string;
}

/** Schéma Supabase (snake_case) pour budget_prices */
export interface BudgetPriceRow {
  id: string;
  stock_region_id: string;
  inventory_id: string;
  tariff_type: BudgetTariffType;
  unit_price_cents: number;
  created_at?: string;
  updated_at?: string;
}

/** Schéma Supabase (snake_case) pour stock_regions */
export interface StockRegionRow {
  id: string;
  owner_id: string;
  name: string;
  created_at?: string;
  updated_at?: string;
}

export interface InventoryRow {
  id: string;
  name: string;
  quantity: number;
  low_stock_threshold?: number | null;
  owner_id?: string | null;
  region_id?: string | null;
  partner_id?: string | null;
  created_at?: string;
  updated_at?: string;
}

export interface LogRow {
  id: string;
  action: string;
  user: string;
  timestamp: string;
  details: string;
  created_at?: string;
  user_id?: string | null;
}

export interface DossierNoteRow {
  id: string;
  dossier_id: string;
  user_id: string;
  user_role: UserRole;
  user_name: string;
  content: string;
  created_at: string;
}

/** Statut d'abonnement Stripe / trial */
export type SubscriptionStatus =
  | "trialing"
  | "active"
  | "past_due"
  | "canceled"
  | "incomplete";

export interface Subscription {
  ownerId: string;
  trialEndsAt: string;
  status: SubscriptionStatus;
  stripeCustomerId?: string | null;
  stripeSubscriptionId?: string | null;
  currentPeriodEnd?: string | null;
  /** Date de création de la ligne abonnement (pour clémence / grandfather). */
  createdAt?: string | null;
}

/** Schéma Supabase (snake_case) pour subscriptions */
export interface SubscriptionRow {
  owner_id: string;
  trial_ends_at: string;
  status: SubscriptionStatus;
  stripe_customer_id?: string | null;
  stripe_subscription_id?: string | null;
  current_period_end?: string | null;
  created_at?: string;
  updated_at?: string;
}

export function subscriptionRowToSubscription(row: SubscriptionRow): Subscription {
  return {
    ownerId: row.owner_id,
    trialEndsAt: row.trial_ends_at,
    status: row.status,
    stripeCustomerId: row.stripe_customer_id ?? undefined,
    stripeSubscriptionId: row.stripe_subscription_id ?? undefined,
    currentPeriodEnd: row.current_period_end ?? undefined,
    createdAt: row.created_at ?? undefined,
  };
}

export function profileToUser(row: ProfileRow): User {
  return {
    id: row.id,
    name: row.name,
    role: row.role,
    code: row.code,
    email: row.email ?? undefined,
    phone: row.phone ?? undefined,
    company: row.company ?? undefined,
    active: row.active,
    is_owner: row.is_owner ?? false,
    team_role: row.team_role ?? undefined,
    permissions: row.permissions ?? undefined,
    owner_id: row.owner_id ?? undefined,
    avatar_url: row.avatar_url ?? undefined,
    preferences: row.preferences ?? undefined,
  };
}

/** Normalise text | text[] | null venant de la DB ou d’un ancien client. */
export function normalizeSignedQuoteUrls(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.map((v) => String(v ?? "").trim()).filter(Boolean);
  }
  if (typeof value === "string" && value.trim()) return [value.trim()];
  return [];
}

export function jobRowToJob(row: JobRow): Job {
  return {
    id: row.id,
    clientName: row.client_name,
    clientPhone: row.client_phone,
    clientAddress: row.client_address,
    status: row.status,
    date: row.date ?? undefined,
    time: row.time ?? undefined,
    installerId: row.installer_id ?? undefined,
    commercialId: row.commercial_id ?? undefined,
    signedQuoteUrls: (() => {
      const urls = normalizeSignedQuoteUrls(row.signed_quote_url);
      return urls.length ? urls : undefined;
    })(),
    partnerId: row.partner_id ?? undefined,
    createdBy: row.created_by ?? undefined,
    notes: row.notes ?? undefined,
    surface: row.surface ?? undefined,
    lightingType: row.lighting_type ?? undefined,
    ceilingHeight: row.ceiling_height ?? undefined,
    estimatedLedCount: row.estimated_led_count ?? undefined,
    stockUsed: row.stock_used ?? undefined,
    stockUsedNeuf: row.stock_used_neuf ?? undefined,
    stockUsedRemplacement: row.stock_used_remplacement ?? undefined,
    partnerInvoiceTotalCents: row.partner_invoice_total_cents ?? undefined,
    stockToPrepare: row.stock_to_prepare ?? undefined,
    stockRegionId: row.stock_region_id ?? undefined,
    dossierType: row.dossier_type ?? undefined,
    dehumidifierCount: row.dehumidifier_count ?? undefined,
    timerCount: row.timer_count ?? undefined,
    needsCounter: row.needs_counter ?? undefined,
    needsGenerator: row.needs_generator ?? undefined,
    installerNotes: row.installer_notes ?? undefined,
    createdAt: row.created_at,
  };
}

export function jobToRow(j: Partial<Job> & { id: string }): Partial<JobRow> & { id: string } {
  return {
    id: j.id,
    client_name: j.clientName ?? "",
    client_phone: j.clientPhone ?? "",
    client_address: j.clientAddress ?? "",
    status: (j.status ?? "pending") as JobRow["status"],
    date: j.date ?? null,
    time: j.time ?? null,
    installer_id: j.installerId?.trim() || null,
    commercial_id: j.commercialId?.trim() || null,
    signed_quote_url: j.signedQuoteUrls?.length
      ? j.signedQuoteUrls.map((p) => p.trim()).filter(Boolean)
      : null,
    partner_id: j.partnerId ?? null,
    created_by: j.createdBy ?? null,
    notes: j.notes ?? null,
    surface: j.surface ?? null,
    lighting_type: j.lightingType ?? null,
    ceiling_height: j.ceilingHeight ?? null,
    estimated_led_count: j.estimatedLedCount ?? null,
    stock_used: j.stockUsed ?? null,
    stock_used_neuf: j.stockUsedNeuf ?? null,
    stock_used_remplacement: j.stockUsedRemplacement ?? null,
    partner_invoice_total_cents: j.partnerInvoiceTotalCents ?? null,
    stock_to_prepare: j.stockToPrepare ?? null,
    stock_region_id: j.stockRegionId ?? null,
    dossier_type: j.dossierType ?? null,
    dehumidifier_count: j.dehumidifierCount ?? null,
    timer_count: j.timerCount ?? null,
    needs_counter: j.needsCounter ?? null,
    needs_generator: j.needsGenerator ?? null,
    installer_notes: j.installerNotes ?? null,
    created_at: j.createdAt ?? new Date().toISOString(),
  };
}

export function inventoryRowToStock(row: InventoryRow): StockItem {
  return {
    id: row.id,
    name: row.name,
    quantity: row.quantity,
    lowStockThreshold: row.low_stock_threshold ?? undefined,
    regionId: row.region_id ?? undefined,
    partnerId: row.partner_id ?? undefined,
  };
}

export function stockRegionRowToRegion(row: StockRegionRow): StockRegion {
  return {
    id: row.id,
    name: row.name,
    ownerId: row.owner_id,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

export function budgetPriceRowToBudgetPrice(row: BudgetPriceRow): BudgetPrice {
  return {
    id: row.id,
    stockRegionId: row.stock_region_id,
    inventoryId: row.inventory_id,
    tariffType: row.tariff_type,
    unitPriceCents: row.unit_price_cents,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

export function budgetPriceToRow(b: Partial<BudgetPrice> & { id: string }): Partial<BudgetPriceRow> & { id: string } {
  return {
    id: b.id,
    stock_region_id: b.stockRegionId ?? "",
    inventory_id: b.inventoryId ?? "",
    tariff_type: (b.tariffType ?? "neuf") as BudgetPriceRow["tariff_type"],
    unit_price_cents: b.unitPriceCents ?? 0,
    created_at: b.createdAt ?? undefined,
    updated_at: b.updatedAt ?? undefined,
  };
}

export function logRowToLog(row: LogRow): Log {
  return {
    id: row.id,
    action: row.action,
    user: row.user,
    timestamp: row.timestamp,
    details: row.details,
  };
}

export function dossierNoteRowToDossierNote(row: DossierNoteRow): DossierNote {
  return {
    id: row.id,
    dossierId: row.dossier_id,
    userId: row.user_id,
    userRole: row.user_role,
    userName: row.user_name,
    content: row.content,
    createdAt: row.created_at,
  };
}
